import { getLocale, resultsPl, runSearch } from "./algolia-client.js";
import { cleanClone, fillCard, readTemplates } from "./hit-mapping.js";

const MIN_QUERY_LENGTH = 3;
const DEBOUNCE_MS = 180;
const MAX_RESULTS = 5;
const LIST_ID = "mx-navbar-search-results";

// Only phones use the full-height cover. Tablet takes the desktop path: navbar.js moves the
// input into the bar, so the panel is a dropdown over the shared overlay there too.
const COVER_QUERY = "(max-width: 767px)";

function getMediaQuery(query) {
  if (typeof window.matchMedia === "function") return window.matchMedia(query);
  return { matches: false, addEventListener() {} };
}

const LABELS = {
  en: { cta: "Learn More", results: (n) => `${n} result${n === 1 ? "" : "s"}`, none: "No results" },
  ua: { cta: "Детальніше", results: (n) => `${n} результатів`, none: "Нічого не знайдено" },
  pl: { cta: "Dowiedz się więcej", results: resultsPl, none: "Brak wyników" },
};

export function initNavbarSearchPreview({ search = runSearch } = {}) {
  const root = document.querySelector(".search__results-nav");
  const panel = document.querySelector(".navbar__search-wrap");
  const input = panel && panel.querySelector("#search-input");
  if (!root || !panel || !input) return null;

  const { card } = readTemplates(root);
  const list = root.querySelector(".search-result-wrap");
  if (!card || !list) {
    console.warn(
      "[mx-search] Navbar preview needs .u-example > .search-result and .search-result-wrap " +
        "inside .search__results-nav.",
      { card: !!card, list: !!list }
    );
    return null;
  }

  // Designer-authored: .search-results-more-wrap holds a real button linking to /search.
  // Only its href and visibility are driven from here.
  const moreWrap = root.querySelector(".search-results-more-wrap");
  const moreLink = moreWrap && moreWrap.querySelector("a");

  const emptyState = root.querySelector("[data-search-empty]");
  if (!emptyState) {
    console.warn(
      "[mx-search] No [data-search-empty] inside .search__results-nav. Add the empty state in " +
        "Webflow with the class search__empty-nav and the attribute data-search-empty."
    );
  }

  const labels = LABELS[getLocale()] || LABELS.en;
  const navbar = root.closest(".navbar");
  const overlay = document.querySelector(".navbar__overlay");
  // Webflow's own click catcher: full viewport below the navbar. Activating it is what makes
  // an outside click land somewhere, instead of measuring the overlay's height.
  const overlayClose = document.querySelector(".navbar__overlay-close");
  const cover = getMediaQuery(COVER_QUERY);

  // The mobile placeholder is authored in Webflow via data-placeholder-mobile, so the copy
  // stays in the Designer and stays localisable per page rather than living in the bundle.
  const fullPlaceholder = input.getAttribute("placeholder") || "";
  const shortPlaceholder = input.getAttribute("data-placeholder-mobile");

  const syncPlaceholder = () => {
    if (!shortPlaceholder) return;
    input.setAttribute("placeholder", cover.matches ? shortPlaceholder : fullPlaceholder);
  };

  syncPlaceholder();
  cover.addEventListener?.("change", syncPlaceholder);

  const liveRegion = document.createElement("div");
  liveRegion.className = "mx-search__live";
  liveRegion.setAttribute("aria-live", "polite");
  liveRegion.setAttribute("role", "status");
  root.appendChild(liveRegion);

  list.id = LIST_ID;
  list.setAttribute("role", "listbox");
  input.setAttribute("role", "combobox");
  input.setAttribute("aria-autocomplete", "list");
  input.setAttribute("aria-controls", LIST_ID);
  input.setAttribute("aria-expanded", "false");

  let debounceTimer = null;
  let heightFrame = null;
  let overlayObserver = null;
  let requestId = 0;
  let isOpen = false;
  let options = [];
  let activeIndex = -1;

  const setEmptyVisible = (visible) => {
    if (emptyState) emptyState.hidden = !visible;
  };

  // Drives .search__btn on mobile. A `:focus + .search__btn` rule cannot do this: tapping
  // the button blurs the input first, so the rule stops matching and pointer-events flips
  // to none mid-tap, swallowing the tap. Keyed to state instead, it stays valid through the
  // blur. Set at every width; the CSS decides where it matters.
  const setSubmittable = (canSubmit) => {
    panel.classList.toggle("is-submittable", canSubmit);
  };

  // The more-wrap lives inside .search-result-wrap so it scrolls with the list, which means
  // the rendered cards have to be removed individually — clearing the list wholesale would
  // delete the Designer's button on the first query.
  const clearCards = () => {
    list.querySelectorAll(":scope > .search-result").forEach((node) => node.remove());
  };

  // Shown only when the index holds more than the page can display, so the button always
  // means "there is more", never "here is the same list again".
  const setMoreVisible = (query, totalHits) => {
    if (!moreWrap) return;

    const hasMore = Boolean(query) && totalHits > MAX_RESULTS;
    moreWrap.hidden = !hasMore;
    if (hasMore && moreLink) {
      moreLink.setAttribute("href", `/search?${new URLSearchParams({ q: query })}`);
    }
  };

  // Same mechanism as .navbar__dropdown: the overlay animates its height (transition on
  // .navbar__overlay), the panel fades in after a delay so the growth lands first.
  // Two frames — the first lets the appended cards lay out, the second measures them.
  // Measured from the overlay's own top to the bottom of the panel. `offsetTop` was wrong
  // once the panel started moving between containers per breakpoint: it is relative to the
  // offset parent, which differs on tablet, so the overlay stopped short of the results.
  const overlayHeight = () => {
    const overlayTop = overlay.getBoundingClientRect().top;
    const rootRect = root.getBoundingClientRect();
    const content = root.firstElementChild;
    const contentHeight = content ? content.getBoundingClientRect().height : 0;

    // Once the list hits its max-height and starts scrolling, the panel stops short of the
    // viewport bottom by the max-height's breathing room. Following it there would leave a
    // strip of page showing under the backdrop, so run to the bottom of the screen instead.
    const scrolls = list.scrollHeight > list.clientHeight + 1;
    const bottom = scrolls
      ? window.innerHeight
      : rootRect.top + Math.max(rootRect.height, root.scrollHeight, contentHeight);

    return `${Math.max(0, Math.round(bottom - overlayTop))}px`;
  };

  const syncOverlay = () => {
    // On a phone the panel is its own full-height sand surface, so the shared backdrop
    // would only double up behind it.
    if (!overlay || cover.matches) return;

    window.cancelAnimationFrame?.(heightFrame);
    heightFrame = window.requestAnimationFrame(() => {
      heightFrame = window.requestAnimationFrame(() => {
        overlay.style.height = isOpen ? overlayHeight() : "0px";
      });
    });
  };

  // `.navbar__overlay` is shared with initNavbarDropdowns, which resets its height and
  // `is-active` on its own schedule (outside clicks, close timers, mobile sync). Rather
  // than depend on knowing every one of those paths, re-assert our state whenever the
  // element is changed underneath us while results are open.
  const watchOverlay = () => {
    if (!overlay || overlayObserver || typeof MutationObserver !== "function") return;

    const options = { attributes: true, attributeFilter: ["style", "class"] };

    overlayObserver = new MutationObserver(() => {
      if (!isOpen) return;

      const lostBackdrop = !overlay.classList.contains("is-active");
      const lostHeight = overlay.style.height === "0px" || overlay.style.height === "";
      if (!lostBackdrop && !lostHeight) return;

      // Detach around our own write. Without this the restore is itself a mutation, and
      // when the measured height resolves to 0 the callback would retrigger forever.
      overlayObserver.disconnect();
      overlay.classList.add("is-active");
      overlay.style.height = overlayHeight();
      overlayObserver.observe(overlay, options);
    });

    overlayObserver.observe(overlay, options);
  };

  // `search-active` tracks the results panel, not the search box: it is on only while
  // results (or the empty state) are on screen.
  const openPanel = () => {
    isOpen = true;
    root.classList.add("is-open");
    navbar?.classList.add("search-active");
    if (!cover.matches) {
      overlay?.classList.add("is-active");
      overlayClose?.classList.add("is-active");
    }
    input.setAttribute("aria-expanded", "true");
    watchOverlay();
    syncOverlay();
  };

  const closePanel = () => {
    if (!isOpen && !root.classList.contains("is-open")) return;

    isOpen = false;
    root.classList.remove("is-open");
    overlay?.classList.remove("is-active");
    overlayClose?.classList.remove("is-active");
    input.setAttribute("aria-expanded", "false");
    syncOverlay();

    // Released last so the guard in initNavbarDropdowns still covers the height reset.
    navbar?.classList.remove("search-active");
  };

  const setActive = (nextIndex) => {
    if (options[activeIndex]) {
      options[activeIndex].classList.remove("is-active");
      options[activeIndex].setAttribute("aria-selected", "false");
    }

    activeIndex = nextIndex;

    const active = options[activeIndex];
    if (!active) {
      input.removeAttribute("aria-activedescendant");
      return;
    }

    active.classList.add("is-active");
    active.setAttribute("aria-selected", "true");
    input.setAttribute("aria-activedescendant", active.id);
  };

  const clearResults = () => {
    window.clearTimeout(debounceTimer);
    requestId += 1;
    clearCards();
    options = [];
    setActive(-1);
    setEmptyVisible(false);
    setMoreVisible("", 0);
    setSubmittable(false);
    liveRegion.textContent = "";
  };

  const closeAndClear = () => {
    clearResults();
    closePanel();
  };

  // Only for an explicit close. Dropping below the character threshold must not wipe what
  // the user is still typing.
  const closeAndReset = () => {
    input.value = "";
    closeAndClear();
  };

  const render = (hits, { query = "", totalHits = 0 } = {}) => {
    clearCards();
    options = [];
    setActive(-1);

    if (!hits.length) {
      setEmptyVisible(true);
      setMoreVisible("", 0);
      setSubmittable(false);
      liveRegion.textContent = labels.none;
      openPanel();
      return;
    }

    setEmptyVisible(false);
    setMoreVisible(query, totalHits);
    setSubmittable(true);

    const fragment = document.createDocumentFragment();
    hits.forEach((hit, index) => {
      const node = fillCard(cleanClone(card), hit, { ctaLabel: labels.cta });
      node.id = `${LIST_ID}-option-${index}`;
      node.setAttribute("role", "option");
      node.setAttribute("aria-selected", "false");
      fragment.appendChild(node);
      options.push(node);
    });

    // Cards go before the button so it stays at the end of the scrolling list.
    list.insertBefore(fragment, moreWrap && moreWrap.parentElement === list ? moreWrap : null);
    liveRegion.textContent = labels.results(hits.length);
    openPanel();
  };

  const dispatch = async (query) => {
    const id = (requestId += 1);

    try {
      const result = await search({ query, hitsPerPage: MAX_RESULTS });
      if (id !== requestId) return;

      // nbHits is the size of the whole result set, not of this page, so "is there more"
      // is exact and costs no second request.
      const hits = ((result && result.hits) || []).slice(0, MAX_RESULTS);
      render(hits, { query, totalHits: (result && result.nbHits) || hits.length });
    } catch (error) {
      if (id !== requestId) return;
      clearResults();
      closePanel();
      console.warn("[mx-search] Navbar query failed", error);
    }
  };

  // `is-open` tracks results only, at every width: focus alone never opens the panel, and
  // dropping back under the threshold closes it again.
  const handleInput = () => {
    const query = input.value.trim();
    window.clearTimeout(debounceTimer);

    if (query.length < MIN_QUERY_LENGTH) {
      closeAndClear();
      return;
    }

    debounceTimer = window.setTimeout(() => dispatch(query), DEBOUNCE_MS);
  };

  input.addEventListener("input", handleInput);

  input.addEventListener("keydown", (event) => {
    if (event.key === "Escape") {
      closeAndReset();
      return;
    }

    if (!isOpen || !options.length) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActive((activeIndex + 1) % options.length);
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActive(activeIndex <= 0 ? options.length - 1 : activeIndex - 1);
      return;
    }

    // Enter without a highlighted option falls through to the existing /search navigation.
    if (event.key === "Enter" && options[activeIndex]) {
      const link = options[activeIndex].querySelector("a.elem__button-wrap");
      if (!link) return;

      event.preventDefault();
      event.stopPropagation();
      link.click();
    }
  });

  // No outside-click handler on purpose. navbar.js keeps the search box open when you
  // click elsewhere, so closing the results alone left the input open with its text while
  // the panel vanished. The two now close together, via the controls below.

  const closeTrigger = panel.querySelector(".navbar__search-close");
  closeTrigger?.addEventListener("click", closeAndReset);

  document.querySelectorAll(".navbar__search--trigger").forEach((trigger) => {
    // Reopening search always starts empty, never with hits from a previous session.
    trigger.addEventListener("click", closeAndReset);
  });

  // Clicking anywhere outside closes the whole search. navbar.js listens for the same click
  // to slide the input shut, so the two halves close together.
  overlayClose?.addEventListener("click", closeAndReset);

  // Closing the mobile menu has to take the results cover with it, otherwise `is-open`
  // survives on a panel whose menu is gone. Delegated because Webflow owns the button.
  document.addEventListener("click", (event) => {
    if (!event.target.closest(".navbar__menu-btn, .w-nav-button")) return;
    closeAndReset();
  });

  window.addEventListener("resize", () => {
    if (isOpen) syncOverlay();
  });

  return { closeAndClear, closeAndReset, isOpen: () => isOpen };
}
